/**
 * Application Form Component
 * Form for creating and editing applications
 */

import React, { useState, useEffect, useRef } from 'react';
import { useValidation } from '../../hooks/useValidation';
import { useAutoFill } from '../../hooks/useAutoFill';
import { SCREENING_FORMATS } from '../../config/constants';
import AutoFillFieldWrapper from '../common/AutoFillFieldWrapper';
import { useNotifications } from '../common/NotificationSystem';
import applicationService from '../../services/applicationService';
import './ApplicationForm.css';

// Maps the selected primary contact person to the email field that feeds MailChimp.
// Mirrors Member.primary_contact_email on the backend (defaults to president email).
const PRIMARY_EMAIL_FIELD = {
  president: 'president_email',
  secretary: 'secretary_email',
  treasurer: 'treasurer_email',
  other: 'other_email',
};
const getPrimaryEmailField = (contactPerson) =>
  PRIMARY_EMAIL_FIELD[contactPerson] || 'president_email';
const isLikelyEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((value || '').trim());

const ApplicationForm = ({
  isOpen,
  onClose,
  onSubmit,
  isProcessing,
  title,
  initialData = null,
  existingApplications = []
}) => {
  const { validateField, validateForm, errors, clearAllErrors } = useValidation();
  const { showError } = useNotifications();
  const modalRef = useRef(null);
  const previousActiveElement = useRef(null);

  // Real-time duplicate primary-contact-email detection.
  // status: 'idle' | 'checking' | 'available' | 'taken'
  const [emailCheck, setEmailCheck] = useState({ status: 'idle', email: '' });
  const emailCheckTimer = useRef(null);
  const lastPopupEmail = useRef(null);

  // Auto-fill functionality
  const {
    suggestions,
    isLoading: autoFillLoading,
    showSuggestions,
    currentField,
    handleFieldChange: handleAutoFillFieldChange,
    applySuggestion,
    dismissSuggestions
  } = useAutoFill(existingApplications, {
    organization_name: { required: true, minLength: 2 },
    current_members_count: { required: true, min: 1 },
    president_name: { required: true, minLength: 2 },
    president_email: { required: true, type: 'email' },
    president_phone: { required: true, type: 'phone' },
    secretary_name: { required: true, minLength: 2 },
    secretary_email: { required: true, type: 'email' },
    secretary_phone: { required: true, type: 'phone' },
    treasurer_name: { required: true, minLength: 2 },
    treasurer_email: { required: true, type: 'email' },
    treasurer_phone: { required: true, type: 'phone' },
    street_address_1: { required: true, minLength: 5 },
    street_address_2: { minLength: 2 },
    city: { required: true, minLength: 2 },
    state: { required: true },
    postcode: { required: true, pattern: /^\d{4}$/ },
    screening_venue: { required: true, minLength: 2 }
  }, 'application');

  // Form state - matching exact API payload structure
  const [formData, setFormData] = useState({
    organization_name: '',
    current_members_count: '',
    street_address_1: '',
    street_address_2: '',
    city: '',
    state: '',
    postcode: '',
    country: 'Australia',
    president_name: '',
    president_phone: '',
    president_email: '',
    secretary_name: '',
    secretary_phone: '',
    secretary_email: '',
    treasurer_name: '',
    treasurer_phone: '',
    treasurer_email: '',
    contact_person: 'president',
    applicant_role: 'president',
    other_role: '',
    other_name: '',
    other_phone: '',
    other_email: '',
    screening_frequency: '',
    screening_format: [],
    screening_venue: '',
    renewal: false,
    release_to_third_parties: true,
    publish_on_website: true,
    agreed_to_conditions: false
  });

  // Initialize form data
  useEffect(() => {
    if (initialData) {
      setFormData({
        organization_name: initialData.organization_name || '',
        current_members_count: initialData.current_members_count || '',
        street_address_1: initialData.street_address_1 || '',
        street_address_2: initialData.street_address_2 || '',
        city: initialData.city || '',
        state: initialData.state || '',
        postcode: initialData.postcode || '',
        country: initialData.country || 'Australia',
        president_name: initialData.president_name || '',
        president_phone: initialData.president_phone || '',
        president_email: initialData.president_email || '',
        secretary_name: initialData.secretary_name || '',
        secretary_phone: initialData.secretary_phone || '',
        secretary_email: initialData.secretary_email || '',
        treasurer_name: initialData.treasurer_name || '',
        treasurer_phone: initialData.treasurer_phone || '',
        treasurer_email: initialData.treasurer_email || '',
        contact_person: initialData.contact_person || 'president',
        applicant_role: initialData.applicant_role || 'president',
        other_role: initialData.other_role || '',
        other_name: initialData.other_name || '',
        other_phone: initialData.other_phone || '',
        other_email: initialData.other_email || '',
        screening_frequency: initialData.screening_frequency || '',
        screening_format: initialData.screening_format || [],
        screening_venue: initialData.screening_venue || '',
        renewal: initialData.renewal || false,
        release_to_third_parties: initialData.release_to_third_parties !== false,
        publish_on_website: initialData.publish_on_website !== false,
        agreed_to_conditions: initialData.agreed_to_conditions || false
      });
    }
  }, [initialData]);

  // Debounced duplicate check: whenever the primary contact email (or which role is
  // primary) changes, ask the backend whether that email is already registered to
  // another member. Mirrors the authoritative serializer check on submit.
  useEffect(() => {
    const primaryEmail = (formData[getPrimaryEmailField(formData.contact_person)] || '').trim();

    if (emailCheckTimer.current) {
      clearTimeout(emailCheckTimer.current);
    }

    if (!isLikelyEmail(primaryEmail)) {
      setEmailCheck({ status: 'idle', email: '' });
      return undefined;
    }

    // When editing, an unchanged primary email is never a duplicate of itself.
    const originalPrimary = initialData
      ? (initialData[getPrimaryEmailField(initialData.contact_person)] || '').trim()
      : '';
    if (initialData && primaryEmail.toLowerCase() === originalPrimary.toLowerCase()) {
      setEmailCheck({ status: 'available', email: primaryEmail });
      return undefined;
    }

    setEmailCheck({ status: 'checking', email: primaryEmail });
    emailCheckTimer.current = setTimeout(async () => {
      const response = await applicationService.checkEmailRegistered(primaryEmail, initialData?.id);
      const exists = response?.success && response?.data?.exists === true;
      // Ignore stale responses if the email changed while the request was in flight.
      setEmailCheck((prev) => (prev.email === primaryEmail
        ? { status: exists ? 'taken' : 'available', email: primaryEmail }
        : prev));
      if (exists && lastPopupEmail.current !== primaryEmail.toLowerCase()) {
        lastPopupEmail.current = primaryEmail.toLowerCase();
        showError(`The email ${primaryEmail} is already registered to another member.`, 'Email already registered');
      }
    }, 500);

    return () => {
      if (emailCheckTimer.current) {
        clearTimeout(emailCheckTimer.current);
      }
    };
    // Intentionally depends only on the contact-email fields, not all of formData,
    // so unrelated keystrokes don't re-trigger the duplicate check.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.contact_person, formData.president_email, formData.secretary_email, formData.treasurer_email, formData.other_email, initialData, showError]);

  // Accessibility: Handle keyboard navigation and focus management
  useEffect(() => {
    if (isOpen) {
      // Store the currently focused element
      previousActiveElement.current = document.activeElement;

      // Prevent background scrolling
      document.body.style.overflow = 'hidden';

      // Focus the modal container
      if (modalRef.current) {
        modalRef.current.focus();
      }

      // Handle ESC key
      const handleKeyDown = (event) => {
        if (event.key === 'Escape' && !isProcessing) {
          if (showSuggestions) {
            dismissSuggestions();
          } else {
            onClose();
          }
        }
      };

      // Handle click outside to dismiss suggestions
      const handleClickOutside = (event) => {
        if (showSuggestions && modalRef.current && !modalRef.current.contains(event.target)) {
          dismissSuggestions();
        }
      };

      document.addEventListener('keydown', handleKeyDown);
      document.addEventListener('click', handleClickOutside);

      return () => {
        // Restore background scrolling
        document.body.style.overflow = 'unset';

        // Restore focus to the previously focused element
        if (previousActiveElement.current) {
          previousActiveElement.current.focus();
        }

        document.removeEventListener('keydown', handleKeyDown);
        document.removeEventListener('click', handleClickOutside);
      };
    }
  }, [isOpen, onClose, isProcessing, showSuggestions, dismissSuggestions]);

  // Handle input changes with auto-fill
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const fieldValue = type === 'checkbox' ? checked : value;

    // Update form data
    setFormData(prev => {
      const updatedFormData = {
        ...prev,
        [name]: fieldValue
      };

      // Clear field error when user starts typing
      if (errors[name]) {
        validateField(name, fieldValue);
      }

      return updatedFormData;
    });
    // Handle auto-fill separately to avoid race conditions
    handleAutoFillForField(name, fieldValue, type);
  };
  const handleAutoFillForField = (name, fieldValue, type) => {
    // Trigger auto-fill only for text-based fields (not checkboxes, selects, etc.)
    if (type === 'text' || type === 'email' || type === 'tel' || type === 'number') {
      setFormData(currentFormData => {
        handleAutoFillFieldChange(name, fieldValue, currentFormData, (field, value) => {
          setFormData(prevFormData => ({
            ...prevFormData,
            [field]: value
          }));
        });
        return currentFormData;
      });
    }
  };

  // Handle screening format changes
  const handleScreeningFormatChange = (format) => {
    setFormData(prev => ({
      ...prev,
      screening_format: prev.screening_format.includes(format)
        ? prev.screening_format.filter(f => f !== format)
        : [...prev.screening_format, format]
    }));
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    clearAllErrors();

    // Validate form - matching API requirements
    const baseRules = {
      organization_name: { required: true, minLength: 2 },
      current_members_count: { required: true, min: 1 },
      street_address_1: { required: true, minLength: 5 },
      city: { required: true, minLength: 2 },
      state: { required: true },
      postcode: { required: true, pattern: /^\d{4}$/ },
      president_name: { required: true, minLength: 2 },
      president_email: { required: true, email: true },
      president_phone: { required: true, phone: true },
      secretary_name: { required: true, minLength: 2 },
      secretary_email: { required: true, email: true },
      secretary_phone: { required: true, phone: true },
      treasurer_name: { required: true, minLength: 2 },
      treasurer_email: { required: true, email: true },
      treasurer_phone: { required: true, phone: true },
      screening_frequency: { required: true },
      screening_format: { required: true, minLength: 1 },
      screening_venue: { required: true, minLength: 2 },
      agreed_to_conditions: { required: true, mustBeTrue: true }
    };

    const rules = { ...baseRules };
    if (formData.contact_person === 'other' || formData.applicant_role === 'other') {
      rules.other_name = { required: true, minLength: 2 };
      rules.other_email = { required: true, email: true };
      rules.other_role = { required: true, minLength: 2 };
      rules.other_phone = { required: true, phone: true };
    }

    const isValid = validateForm(formData, rules);

    if (!isValid) {
      return;
    }

    // Block submission if the primary contact email is already registered.
    // The backend enforces this too, but this avoids a wasted round-trip.
    if (emailCheck.status === 'taken') {
      showError('This email is already registered to another member. Please use a different email.', 'Email already registered');
      return;
    }

    // Convert current_members_count to number
    const submitData = {
      ...formData,
      current_members_count: parseInt(formData.current_members_count) || 0
    };

    await onSubmit(submitData);
  };

  if (!isOpen) return null;

  return (
    <div
      className="modal-overlay"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
    >
      <div
        className="modal-container"
        onClick={(e) => e.stopPropagation()}
        ref={modalRef}
        tabIndex={-1}
      >
        {/* Header */}
        <div className="modal-header">
          <h2 id="modal-title">{title}</h2>
          <button
            className="close-btn"
            onClick={onClose}
            disabled={isProcessing}
            aria-label="Close modal"
            type="button"
          >
            ×
          </button>
        </div>

        {/* Body */}
        <div className="modal-body">
          <form onSubmit={handleSubmit}>
            {/* Organization Information */}
            <div className="form-section">
              <h4>Organization Information</h4>
              <div className="form-row">
                <AutoFillFieldWrapper
                  fieldName="organization_name"
                  suggestions={suggestions}
                  isLoading={autoFillLoading}
                  showSuggestions={showSuggestions}
                  currentField={currentField}
                  onApplyField={(field, value) => applySuggestion(field, value, (field, value) => {
                    setFormData(prev => ({ ...prev, [field]: value }));
                  })}
                  onIgnoreField={(field) => applySuggestion(field, null, () => { })}
                >
                  <div className="form-group">
                    <label htmlFor="organization_name">Organization Name *</label>
                    <input
                      type="text"
                      id="organization_name"
                      name="organization_name"
                      value={formData.organization_name}
                      onChange={handleInputChange}
                      className={errors.organization_name ? 'error' : ''}
                      disabled={isProcessing}
                      placeholder="e.g., Melbourne Film Society"
                    />
                    {errors.organization_name && (
                      <span className="error-message">{errors.organization_name}</span>
                    )}
                  </div>
                </AutoFillFieldWrapper>
                <AutoFillFieldWrapper
                  fieldName="current_members_count"
                  suggestions={suggestions}
                  isLoading={autoFillLoading}
                  showSuggestions={showSuggestions}
                  currentField={currentField}
                  onApplyField={(field, value) => applySuggestion(field, value, (field, value) => {
                    setFormData(prev => ({ ...prev, [field]: value }));
                  })}
                  onIgnoreField={(field) => applySuggestion(field, null, () => { })}
                >
                  <div className="form-group">
                    <label htmlFor="current_members_count">Current Members Count *</label>
                    <input
                      type="number"
                      id="current_members_count"
                      name="current_members_count"
                      value={formData.current_members_count}
                      onChange={handleInputChange}
                      className={errors.current_members_count ? 'error' : ''}
                      disabled={isProcessing}
                      min="1"
                      placeholder="e.g., 30"
                    />
                    {errors.current_members_count && (
                      <span className="error-message">{errors.current_members_count}</span>
                    )}
                  </div>
                </AutoFillFieldWrapper>
              </div>
            </div>

            {/* Address Information */}
            <div className="form-section">
              <h4>Address Information</h4>
              <div className="form-row">
                <AutoFillFieldWrapper
                  fieldName="street_address_1"
                  suggestions={suggestions}
                  isLoading={autoFillLoading}
                  showSuggestions={showSuggestions}
                  currentField={currentField}
                  onApplyField={(field, value) => applySuggestion(field, value, (field, value) => {
                    setFormData(prev => ({ ...prev, [field]: value }));
                  })}
                  onIgnoreField={(field) => applySuggestion(field, null, () => { })}
                >
                  <div className="form-group">
                    <label htmlFor="street_address_1">Street Address 1 *</label>
                    <input
                      type="text"
                      id="street_address_1"
                      name="street_address_1"
                      value={formData.street_address_1}
                      onChange={handleInputChange}
                      className={errors.street_address_1 ? 'error' : ''}
                      disabled={isProcessing}
                      placeholder="e.g., 456 Movie Lane"
                    />
                    {errors.street_address_1 && (
                      <span className="error-message">{errors.street_address_1}</span>
                    )}
                  </div>
                </AutoFillFieldWrapper>
                <AutoFillFieldWrapper
                  fieldName="street_address_2"
                  suggestions={suggestions}
                  isLoading={autoFillLoading}
                  showSuggestions={showSuggestions}
                  currentField={currentField}
                  onApplyField={(field, value) => applySuggestion(field, value, (field, value) => {
                    setFormData(prev => ({ ...prev, [field]: value }));
                  })}
                  onIgnoreField={(field) => applySuggestion(field, null, () => { })}
                >
                  <div className="form-group">
                    <label htmlFor="street_address_2">Street Address 2</label>
                    <input
                      type="text"
                      id="street_address_2"
                      name="street_address_2"
                      value={formData.street_address_2}
                      onChange={handleInputChange}
                      className={errors.street_address_2 ? 'error' : ''}
                      disabled={isProcessing}
                      placeholder="e.g., Suite 2"
                    />
                    {errors.street_address_2 && (
                      <span className="error-message">{errors.street_address_2}</span>
                    )}
                  </div>
                </AutoFillFieldWrapper>
              </div>
              <div className="form-row">
                <AutoFillFieldWrapper
                  fieldName="city"
                  suggestions={suggestions}
                  isLoading={autoFillLoading}
                  showSuggestions={showSuggestions}
                  currentField={currentField}
                  onApplyField={(field, value) => applySuggestion(field, value, (field, value) => {
                    setFormData(prev => ({ ...prev, [field]: value }));
                  })}
                  onIgnoreField={(field) => applySuggestion(field, null, () => { })}
                >
                  <div className="form-group">
                    <label htmlFor="city">City *</label>
                    <input
                      type="text"
                      id="city"
                      name="city"
                      value={formData.city}
                      onChange={handleInputChange}
                      className={errors.city ? 'error' : ''}
                      disabled={isProcessing}
                      placeholder="e.g., Melbourne"
                    />
                    {errors.city && (
                      <span className="error-message">{errors.city}</span>
                    )}
                  </div>
                </AutoFillFieldWrapper>
                <AutoFillFieldWrapper
                  fieldName="state"
                  suggestions={suggestions}
                  isLoading={autoFillLoading}
                  showSuggestions={showSuggestions}
                  currentField={currentField}
                  onApplyField={(field, value) => applySuggestion(field, value, (field, value) => {
                    setFormData(prev => ({ ...prev, [field]: value }));
                  })}
                  onIgnoreField={(field) => applySuggestion(field, null, () => { })}
                >
                  <div className="form-group">
                    <label htmlFor="state">State *</label>
                    <select
                      id="state"
                      name="state"
                      value={formData.state}
                      onChange={handleInputChange}
                      className={errors.state ? 'error' : ''}
                      disabled={isProcessing}
                    >
                      <option value="">Select State</option>
                      <option value="NSW">New South Wales</option>
                      <option value="VIC">Victoria</option>
                      <option value="QLD">Queensland</option>
                      <option value="WA">Western Australia</option>
                      <option value="SA">South Australia</option>
                      <option value="TAS">Tasmania</option>
                      <option value="ACT">Australian Capital Territory</option>
                      <option value="NT">Northern Territory</option>
                    </select>
                    {errors.state && (
                      <span className="error-message">{errors.state}</span>
                    )}
                  </div>
                </AutoFillFieldWrapper>
                <AutoFillFieldWrapper
                  fieldName="postcode"
                  suggestions={suggestions}
                  isLoading={autoFillLoading}
                  showSuggestions={showSuggestions}
                  currentField={currentField}
                  onApplyField={(field, value) => applySuggestion(field, value, (field, value) => {
                    setFormData(prev => ({ ...prev, [field]: value }));
                  })}
                  onIgnoreField={(field) => applySuggestion(field, null, () => { })}
                >
                  <div className="form-group">
                    <label htmlFor="postcode">Postcode *</label>
                    <input
                      type="text"
                      id="postcode"
                      name="postcode"
                      value={formData.postcode}
                      onChange={handleInputChange}
                      className={errors.postcode ? 'error' : ''}
                      disabled={isProcessing}
                      placeholder="e.g., 3000"
                      pattern="[0-9]{4}"
                      maxLength="4"
                    />
                    {errors.postcode && (
                      <span className="error-message">{errors.postcode}</span>
                    )}
                  </div>
                </AutoFillFieldWrapper>
              </div>
            </div>

            {/* Executive Committee Information */}
            <div className="form-section">
              <h4>Executive Committee Information</h4>

              {/* President */}
              <div className="committee-member">
                <h5>President</h5>
                <div className="form-row">
                  <AutoFillFieldWrapper
                    fieldName="president_name"
                    suggestions={suggestions}
                    isLoading={autoFillLoading}
                    showSuggestions={showSuggestions}
                    currentField={currentField}
                    onApplyField={(field, value) => applySuggestion(field, value, (field, value) => {
                      setFormData(prev => ({ ...prev, [field]: value }));
                    })}
                    onIgnoreField={(field) => applySuggestion(field, null, () => { })}
                  >
                    <div className="form-group">
                      <label htmlFor="president_name">President Name *</label>
                      <input
                        type="text"
                        id="president_name"
                        name="president_name"
                        value={formData.president_name}
                        onChange={handleInputChange}
                        className={errors.president_name ? 'error' : ''}
                        disabled={isProcessing}
                        placeholder="e.g., Alice Brown"
                      />
                      {errors.president_name && (
                        <span className="error-message">{errors.president_name}</span>
                      )}
                    </div>
                  </AutoFillFieldWrapper>
                  <AutoFillFieldWrapper
                    fieldName="president_email"
                    suggestions={suggestions}
                    isLoading={autoFillLoading}
                    showSuggestions={showSuggestions}
                    currentField={currentField}
                    onApplyField={(field, value) => applySuggestion(field, value, (field, value) => {
                      setFormData(prev => ({ ...prev, [field]: value }));
                    })}
                    onIgnoreField={(field) => applySuggestion(field, null, () => { })}
                  >
                    <div className="form-group">
                      <label htmlFor="president_email">President Email *</label>
                      <input
                        type="email"
                        id="president_email"
                        name="president_email"
                        value={formData.president_email}
                        onChange={handleInputChange}
                        className={errors.president_email ? 'error' : ''}
                        disabled={isProcessing}
                        placeholder="e.g., president@melbournefilmsociety.com"
                      />
                      {errors.president_email && (
                        <span className="error-message">{errors.president_email}</span>
                      )}
                      {emailCheck.status === 'taken' && getPrimaryEmailField(formData.contact_person) === 'president_email' && (
                        <span className="error-message">This email is already registered to another member.</span>
                      )}
                    </div>
                  </AutoFillFieldWrapper>
                  <AutoFillFieldWrapper
                    fieldName="president_phone"
                    suggestions={suggestions}
                    isLoading={autoFillLoading}
                    showSuggestions={showSuggestions}
                    currentField={currentField}
                    onApplyField={(field, value) => applySuggestion(field, value, (field, value) => {
                      setFormData(prev => ({ ...prev, [field]: value }));
                    })}
                    onIgnoreField={(field) => applySuggestion(field, null, () => { })}
                  >
                    <div className="form-group">
                      <label htmlFor="president_phone">President Phone *</label>
                      <input
                        type="tel"
                        id="president_phone"
                        name="president_phone"
                        value={formData.president_phone}
                        onChange={handleInputChange}
                        className={errors.president_phone ? 'error' : ''}
                        disabled={isProcessing}
                        placeholder="e.g., 0412345678"
                      />
                      {errors.president_phone && (
                        <span className="error-message">{errors.president_phone}</span>
                      )}
                    </div>
                  </AutoFillFieldWrapper>
                </div>
              </div>

              {/* Secretary */}
              <div className="committee-member">
                <h5>Secretary</h5>
                <div className="form-row">
                  <AutoFillFieldWrapper
                    fieldName="secretary_name"
                    suggestions={suggestions}
                    isLoading={autoFillLoading}
                    showSuggestions={showSuggestions}
                    currentField={currentField}
                    onApplyField={(field, value) => applySuggestion(field, value, (field, value) => {
                      setFormData(prev => ({ ...prev, [field]: value }));
                    })}
                    onIgnoreField={(field) => applySuggestion(field, null, () => { })}
                  >
                    <div className="form-group">
                      <label htmlFor="secretary_name">Secretary Name *</label>
                      <input
                        type="text"
                        id="secretary_name"
                        name="secretary_name"
                        value={formData.secretary_name}
                        onChange={handleInputChange}
                        className={errors.secretary_name ? 'error' : ''}
                        disabled={isProcessing}
                        placeholder="e.g., Charlie Wilson"
                      />
                      {errors.secretary_name && (
                        <span className="error-message">{errors.secretary_name}</span>
                      )}
                    </div>
                  </AutoFillFieldWrapper>
                  <AutoFillFieldWrapper
                    fieldName="secretary_email"
                    suggestions={suggestions}
                    isLoading={autoFillLoading}
                    showSuggestions={showSuggestions}
                    currentField={currentField}
                    onApplyField={(field, value) => applySuggestion(field, value, (field, value) => {
                      setFormData(prev => ({ ...prev, [field]: value }));
                    })}
                    onIgnoreField={(field) => applySuggestion(field, null, () => { })}
                  >
                    <div className="form-group">
                      <label htmlFor="secretary_email">Secretary Email *</label>
                      <input
                        type="email"
                        id="secretary_email"
                        name="secretary_email"
                        value={formData.secretary_email}
                        onChange={handleInputChange}
                        className={errors.secretary_email ? 'error' : ''}
                        disabled={isProcessing}
                        placeholder="e.g., secretary@melbournefilmsociety.com"
                      />
                      {errors.secretary_email && (
                        <span className="error-message">{errors.secretary_email}</span>
                      )}
                      {emailCheck.status === 'taken' && getPrimaryEmailField(formData.contact_person) === 'secretary_email' && (
                        <span className="error-message">This email is already registered to another member.</span>
                      )}
                    </div>
                  </AutoFillFieldWrapper>
                  <AutoFillFieldWrapper
                    fieldName="secretary_phone"
                    suggestions={suggestions}
                    isLoading={autoFillLoading}
                    showSuggestions={showSuggestions}
                    currentField={currentField}
                    onApplyField={(field, value) => applySuggestion(field, value, (field, value) => {
                      setFormData(prev => ({ ...prev, [field]: value }));
                    })}
                    onIgnoreField={(field) => applySuggestion(field, null, () => { })}
                  >
                    <div className="form-group">
                      <label htmlFor="secretary_phone">Secretary Phone *</label>
                      <input
                        type="tel"
                        id="secretary_phone"
                        name="secretary_phone"
                        value={formData.secretary_phone}
                        onChange={handleInputChange}
                        className={errors.secretary_phone ? 'error' : ''}
                        disabled={isProcessing}
                        placeholder="e.g., 0412345679"
                      />
                      {errors.secretary_phone && (
                        <span className="error-message">{errors.secretary_phone}</span>
                      )}
                    </div>
                  </AutoFillFieldWrapper>
                </div>
              </div>

              {/* Treasurer */}
              <div className="committee-member">
                <h5>Treasurer</h5>
                <div className="form-row">
                  <AutoFillFieldWrapper
                    fieldName="treasurer_name"
                    suggestions={suggestions}
                    isLoading={autoFillLoading}
                    showSuggestions={showSuggestions}
                    currentField={currentField}
                    onApplyField={(field, value) => applySuggestion(field, value, (field, value) => {
                      setFormData(prev => ({ ...prev, [field]: value }));
                    })}
                    onIgnoreField={(field) => applySuggestion(field, null, () => { })}
                  >
                    <div className="form-group">
                      <label htmlFor="treasurer_name">Treasurer Name *</label>
                      <input
                        type="text"
                        id="treasurer_name"
                        name="treasurer_name"
                        value={formData.treasurer_name}
                        onChange={handleInputChange}
                        className={errors.treasurer_name ? 'error' : ''}
                        disabled={isProcessing}
                        placeholder="e.g., Diana Lee"
                      />
                      {errors.treasurer_name && (
                        <span className="error-message">{errors.treasurer_name}</span>
                      )}
                    </div>
                  </AutoFillFieldWrapper>
                  <AutoFillFieldWrapper
                    fieldName="treasurer_email"
                    suggestions={suggestions}
                    isLoading={autoFillLoading}
                    showSuggestions={showSuggestions}
                    currentField={currentField}
                    onApplyField={(field, value) => applySuggestion(field, value, (field, value) => {
                      setFormData(prev => ({ ...prev, [field]: value }));
                    })}
                    onIgnoreField={(field) => applySuggestion(field, null, () => { })}
                  >
                    <div className="form-group">
                      <label htmlFor="treasurer_email">Treasurer Email *</label>
                      <input
                        type="email"
                        id="treasurer_email"
                        name="treasurer_email"
                        value={formData.treasurer_email}
                        onChange={handleInputChange}
                        className={errors.treasurer_email ? 'error' : ''}
                        disabled={isProcessing}
                        placeholder="e.g., treasurer@melbournefilmsociety.com"
                      />
                      {errors.treasurer_email && (
                        <span className="error-message">{errors.treasurer_email}</span>
                      )}
                      {emailCheck.status === 'taken' && getPrimaryEmailField(formData.contact_person) === 'treasurer_email' && (
                        <span className="error-message">This email is already registered to another member.</span>
                      )}
                    </div>
                  </AutoFillFieldWrapper>
                  <AutoFillFieldWrapper
                    fieldName="treasurer_phone"
                    suggestions={suggestions}
                    isLoading={autoFillLoading}
                    showSuggestions={showSuggestions}
                    currentField={currentField}
                    onApplyField={(field, value) => applySuggestion(field, value, (field, value) => {
                      setFormData(prev => ({ ...prev, [field]: value }));
                    })}
                    onIgnoreField={(field) => applySuggestion(field, null, () => { })}
                  >
                    <div className="form-group">
                      <label htmlFor="treasurer_phone">Treasurer Phone *</label>
                      <input
                        type="tel"
                        id="treasurer_phone"
                        name="treasurer_phone"
                        value={formData.treasurer_phone}
                        onChange={handleInputChange}
                        className={errors.treasurer_phone ? 'error' : ''}
                        disabled={isProcessing}
                        placeholder="e.g., 0412345680"
                      />
                      {errors.treasurer_phone && (
                        <span className="error-message">{errors.treasurer_phone}</span>
                      )}
                    </div>
                  </AutoFillFieldWrapper>
                </div>
              </div>
            </div>

            {/* Screening Information */}
            <div className="form-section">
              <h4>Screening Information</h4>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="screening_frequency">Screening Frequency *</label>
                  <select
                    id="screening_frequency"
                    name="screening_frequency"
                    value={formData.screening_frequency}
                    onChange={handleInputChange}
                    className={errors.screening_frequency ? 'error' : ''}
                    disabled={isProcessing}
                  >
                    <option value="">Select Frequency</option>
                    <option value="Weekly">Weekly</option>
                    <option value="Bi-weekly">Bi-weekly</option>
                    <option value="Monthly">Monthly</option>
                    <option value="Bi-monthly">Bi-monthly</option>
                    <option value="Quarterly">Quarterly</option>
                    <option value="Irregular">Irregular</option>
                  </select>
                  {errors.screening_frequency && (
                    <span className="error-message">{errors.screening_frequency}</span>
                  )}
                </div>
                <AutoFillFieldWrapper
                  fieldName="screening_venue"
                  suggestions={suggestions}
                  isLoading={autoFillLoading}
                  showSuggestions={showSuggestions}
                  currentField={currentField}
                  onApplyField={(field, value) => applySuggestion(field, value, (field, value) => {
                    setFormData(prev => ({ ...prev, [field]: value }));
                  })}
                  onIgnoreField={(field) => applySuggestion(field, null, () => { })}
                >
                  <div className="form-group">
                    <label htmlFor="screening_venue">Screening Venue *</label>
                    <input
                      type="text"
                      id="screening_venue"
                      name="screening_venue"
                      value={formData.screening_venue}
                      onChange={handleInputChange}
                      className={errors.screening_venue ? 'error' : ''}
                      disabled={isProcessing}
                      placeholder="e.g., Melbourne Cinema"
                    />
                    {errors.screening_venue && (
                      <span className="error-message">{errors.screening_venue}</span>
                    )}
                  </div>
                </AutoFillFieldWrapper>
              </div>
              <div className="form-row">
                <div className="form-group full-width">
                  <label>Screening Formats *</label>
                  <p className="field-description">
                    Select all the formats your organization uses for film screenings. You can choose multiple options.
                  </p>
                  <div className="screening-formats-grid">
                    {Object.entries(SCREENING_FORMATS).map(([key, value]) => (
                      <div key={key} className="format-option">
                        <label className="format-checkbox-label">
                          <input
                            type="checkbox"
                            checked={formData.screening_format.includes(value)}
                            onChange={() => handleScreeningFormatChange(value)}
                            disabled={isProcessing}
                          />
                          <div className="format-checkbox-content">
                            <div className="format-icon">
                              {value === '16mm' && '🎬'}
                              {value === '35mm' && '🎞️'}
                              {value === 'dvd' && '💿'}
                              {value === 'blu_ray' && '💿'}
                              {value === '4k' && '📺'}
                              {value === 'digital' && '💻'}
                              {value === 'other' && '🔧'}
                            </div>
                            <div className="format-details">
                              <span className="format-name">{key}</span>
                              <span className="format-description">
                                {value === '16mm' && 'Traditional 16mm film projection'}
                                {value === '35mm' && 'Classic 35mm film projection'}
                                {value === 'dvd' && 'Standard DVD playback'}
                                {value === 'blu_ray' && 'High-definition Blu-ray disc'}
                                {value === '4k' && 'Ultra-high definition 4K content'}
                                {value === 'digital' && 'Digital files and streaming'}
                                {value === 'other' && 'Other screening formats'}
                              </span>
                            </div>
                          </div>
                        </label>
                      </div>
                    ))}
                  </div>
                  {errors.screening_format && (
                    <span className="error-message">{errors.screening_format}</span>
                  )}
                </div>
              </div>
            </div>

            {/* Contact Preferences */}
            <div className="form-section">
              <h4>Contact Preferences</h4>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="contact_person">Primary Contact Person *</label>
                  <select
                    id="contact_person"
                    name="contact_person"
                    value={formData.contact_person}
                    onChange={handleInputChange}
                    className={errors.contact_person ? 'error' : ''}
                    disabled={isProcessing}
                  >
                    <option value="president">President</option>
                    <option value="secretary">Secretary</option>
                    <option value="treasurer">Treasurer</option>
                    <option value="other">Other</option>
                  </select>
                  {errors.contact_person && (
                    <span className="error-message">{errors.contact_person}</span>
                  )}
                </div>
                <div className="form-group">
                  <label htmlFor="applicant_role">Applicant Role *</label>
                  <select
                    id="applicant_role"
                    name="applicant_role"
                    value={formData.applicant_role}
                    onChange={handleInputChange}
                    className={errors.applicant_role ? 'error' : ''}
                    disabled={isProcessing}
                  >
                    <option value="president">President</option>
                    <option value="secretary">Secretary</option>
                    <option value="treasurer">Treasurer</option>
                    <option value="other">Other</option>
                  </select>
                  {errors.applicant_role && (
                    <span className="error-message">{errors.applicant_role}</span>
                  )}
                </div>
              </div>
            
              {(formData.contact_person === 'other' || formData.applicant_role === 'other') && (
                <>
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="other_name">Other Contact Name *</label>
                      <input
                        type="text"
                        id="other_name"
                        name="other_name"
                        value={formData.other_name}
                        onChange={handleInputChange}
                        className={errors.other_name ? 'error' : ''}
                        disabled={isProcessing}
                        placeholder="e.g., Jordan Smith"
                      />
                      {errors.other_name && (
                        <span className="error-message">{errors.other_name}</span>
                      )}
                    </div>
                    <div className="form-group">
                      <label htmlFor="other_role">Other Role *</label>
                      <input
                        type="text"
                        id="other_role"
                        name="other_role"
                        value={formData.other_role}
                        onChange={handleInputChange}
                        className={errors.other_role ? 'error' : ''}
                        disabled={isProcessing}
                        placeholder="e.g., Communications Officer"
                      />
                      {errors.other_role && (
                        <span className="error-message">{errors.other_role}</span>
                      )}
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="other_email">Other Email *</label>
                      <input
                        type="email"
                        id="other_email"
                        name="other_email"
                        value={formData.other_email}
                        onChange={handleInputChange}
                        className={errors.other_email ? 'error' : ''}
                        disabled={isProcessing}
                        placeholder="e.g., contact@filmsociety.com"
                      />
                      {errors.other_email && (
                        <span className="error-message">{errors.other_email}</span>
                      )}
                      {emailCheck.status === 'taken' && getPrimaryEmailField(formData.contact_person) === 'other_email' && (
                        <span className="error-message">This email is already registered to another member.</span>
                      )}
                    </div>
                    <div className="form-group">
                      <label htmlFor="other_phone">Other Phone *</label>
                      <input
                        type="tel"
                        id="other_phone"
                        name="other_phone"
                        value={formData.other_phone}
                        onChange={handleInputChange}
                        className={errors.other_phone ? 'error' : ''}
                        disabled={isProcessing}
                        placeholder="e.g., 0412 345 678"
                      />
                      {errors.other_phone && (
                        <span className="error-message">{errors.other_phone}</span>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Application Settings & Permissions */}
            <div className="form-section">
              <h4>Application Settings & Permissions</h4>
              <p className="section-description">
                Configure application settings and permissions for the organization.
              </p>

              <div className="settings-grid">
                {/* Application Type */}
                <div className="setting-group">
                  <h5>Application Type</h5>
                  <div className="setting-option">
                    <label className="setting-checkbox-label">
                      <input
                        type="checkbox"
                        name="renewal"
                        checked={formData.renewal}
                        onChange={handleInputChange}
                        disabled={isProcessing}
                      />
                      <div className="setting-content">
                        <span className="setting-title">Renewal Application</span>
                        <span className="setting-description">
                          Check if this is a renewal of an existing membership
                        </span>
                      </div>
                    </label>
                  </div>
                </div>

                {/* Data Sharing Preferences */}
                <div className="setting-group">
                  <h5>Data Sharing Preferences</h5>
                  <div className="setting-option">
                    <label className="setting-checkbox-label">
                      <input
                        type="checkbox"
                        name="release_to_third_parties"
                        checked={formData.release_to_third_parties}
                        onChange={handleInputChange}
                        disabled={isProcessing}
                      />
                      <div className="setting-content">
                        <span className="setting-title">Release to Third Parties</span>
                        <span className="setting-description">
                          Allow sharing organization information with partner organizations
                        </span>
                      </div>
                    </label>
                  </div>

                  <div className="setting-option">
                    <label className="setting-checkbox-label">
                      <input
                        type="checkbox"
                        name="publish_on_website"
                        checked={formData.publish_on_website}
                        onChange={handleInputChange}
                        disabled={isProcessing}
                      />
                      <div className="setting-content">
                        <span className="setting-title">Publish on Website</span>
                        <span className="setting-description">
                          Display organization information on the public website directory
                        </span>
                      </div>
                    </label>
                  </div>
                </div>

                {/* Terms and Conditions */}
                <div className="setting-group terms-group">
                  <h5>Terms and Conditions</h5>
                  <div className="setting-option required">
                    <label className="setting-checkbox-label required">
                      <input
                        type="checkbox"
                        name="agreed_to_conditions"
                        checked={formData.agreed_to_conditions}
                        onChange={handleInputChange}
                        className={errors.agreed_to_conditions ? 'error' : ''}
                        disabled={isProcessing}
                      />
                      <div className="setting-content">
                        <span className="setting-title">Agree to Terms and Conditions *</span>
                        <span className="setting-description">
                          I confirm that I have read and agree to the membership terms and conditions
                        </span>
                      </div>
                    </label>
                    {errors.agreed_to_conditions && (
                      <span className="error-message">{errors.agreed_to_conditions}</span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={onClose}
                disabled={isProcessing}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={isProcessing || emailCheck.status === 'taken' || emailCheck.status === 'checking'}
              >
                {isProcessing ? 'Processing...' : (initialData ? 'Update Application' : 'Create Application')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ApplicationForm;
